// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { FormControl, Validators } from '@angular/forms';
import { MatGridListModule } from '@angular/material/grid-list';
import { Analysis } from "../../../../interfaces/analysis";
import {
  MoleculeListData,
  MoleculeSizeDistribution,
  DistributionValue,
  DistributionTick,
  IntervalCountPerMapDistribution,
} from '../../../../interfaces/hce/hce-stats';
import { MessageModalService } from '../../../../services/message-modal/message-modal.service';
import { HceStatsService } from '../../../../services/hce/hce-stats/hce-stats.service';
import { ButtonsComponent } from '../../../../shared/buttons/buttons.component';
import { DropdownComponent } from '../../../../shared/dropdown/dropdown.component';
import { HceStatsHistogramComponent } from '../stats-histogram/hce-stats-histogram.component';
import { HceStatsTableComponent } from '../stats-table/hce-stats-table.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-hce-stats-view-molecule',
  standalone: true,
  imports: [
    HceStatsHistogramComponent,
    HceStatsTableComponent,
    DropdownComponent,
    MatGridListModule,
    ButtonsComponent,
    MatCardModule,
    MatProgressSpinnerModule,
    NgIf,
],
  templateUrl: './hce-stats-view-molecule.component.html',
  styleUrl: './hce-stats-view-molecule.component.css',
})
export class HceStatsViewMoleculeComponent {
  @Input() analysis!: Analysis | null;

  constructor(
    public dialog: MatDialog,
    private statsService: HceStatsService,
    private messageModal: MessageModalService
  ) {}

  /*
   * Genome List
   */

  gender: string = '';

  // molecule List
  moleculeDisplayedColumnLabels: string[] = [
    'Item',
    'Statistic',
  ];
  moleculeDisplayedColumns: string[] = [
    'key',
    'value',
  ];
  moleculeListData?: MoleculeListData = [];

  /*
   * Bar chart
   */

  // Change x-axis
  dropdownOptions: Array<{ value: string; label: string }> =[
    {value: '0', label:"1000"},
    {value: '1', label:"800"},
    {value: '2', label:"600"},
    {value: '3', label:"400"},
    {value: '4', label:"200"},
  ];

  // Change y-axis
  btnName: string = 'CHANGE Y-AXIS';
  btnMatStroked = 'mat-stroked-button';
  btnStyleStroked = 'btn-stroked';

  // Display settings for the bar chart
  selectionValue: string = '3';
  selectPanelName = new FormControl(this.selectionValue, [Validators.required]);
  isYAxisPV: boolean = true; 
  barColor = '#4BB2C5';

  // Molecule Size Distribution
  moleculeSizeDistribution?: MoleculeSizeDistribution = {};
  moleculeSizeDistributionData?: DistributionValue = [];
  moleculeSizeDistributionXTicks?: DistributionTick = [];
  moleculeSizeDistributionYTicks?: DistributionTick = undefined;

  // Interval Count Distribution
  intervalCountDistribution?: IntervalCountPerMapDistribution = {};
  intervalCountDistributionData?: DistributionValue = [];
  intervalCountDistributionXTicks?: DistributionTick = [];
  intervalCountDistributionYTicks?: DistributionTick = undefined;

  isLoading = true;
  hasError = false;

  /*
   * X-axis dropdown change event
   */
  onChangeXAxis(value: string) {
    this.selectionValue = value;
    this.changeAxisParam(Number(value), this.isYAxisPV)
  }

  /*
   * Y-axis Button click event
   */
  onClickChangeYAxis() {
    this.isYAxisPV = !this.isYAxisPV;
    this.changeAxisParam(Number(this.selectionValue), this.isYAxisPV)
  }

  private changeAxisParam(xAxis: number, isYAxisPV: boolean) {
    // Molecule Size Distribution
    this.moleculeSizeDistributionXTicks = this.moleculeSizeDistribution?.xtick?.[xAxis];
    if (isYAxisPV) {
      this.moleculeSizeDistributionData = this?.moleculeSizeDistribution?.p_v;
      this.moleculeSizeDistributionYTicks = undefined;
    } else {
      this.moleculeSizeDistributionData = this?.moleculeSizeDistribution?.c_v;
      this.moleculeSizeDistributionYTicks = this?.moleculeSizeDistribution?.ytick;
    }

    // Interval Count Distribution
    this.intervalCountDistributionXTicks = this?.intervalCountDistribution?.xtick?.[xAxis];
  }
  
  ngOnInit() {
    this.isLoading = true;
    this.loadStatsInput();
    this.changeAxisParam(Number(this.selectionValue), this.isYAxisPV);
  }

  private showLoadFailed(): void {
    this.messageModal.openModal(
      $localize`Error`,
      $localize`Failed to load molecule statistics.`
    );
  }

  private loadStatsInput() {
    if (this?.analysis?.PanelID == null) {
      console.error('Panel ID is empty');
      this.showLoadFailed();
      this.isLoading = false;
      this.hasError = true;
      return;
    }

    this.statsService.getStatsMolecule(this.analysis.AnalysisID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.gender = response?.gender ?? '-';

          // molecule List
          this.moleculeListData = response?.moleculeListData;

          // Molecule Size Distribution
          this.moleculeSizeDistribution = response.moleculeSizeDistribution;
          this.moleculeSizeDistributionData ??= this?.moleculeSizeDistribution?.p_v;
          this.moleculeSizeDistributionXTicks = this?.moleculeSizeDistribution?.xtick?.[Number(this.selectionValue)] ?? [];
          this.moleculeSizeDistributionYTicks = undefined;

          // Interval Count Distribution
          this.intervalCountDistribution = response?.intervalCountDistribution;
          this.intervalCountDistributionData = this?.intervalCountDistribution?.v;
          this.intervalCountDistributionXTicks = this?.intervalCountDistribution?.xtick?.[Number(this.selectionValue)] ?? [];
          this.intervalCountDistributionYTicks ??= this?.intervalCountDistribution?.ytick;
        } else {
          if (Array.isArray(response?.errorMessage) && response.errorMessage.length > 0) {
            console.error(response.errorMessage.join(', '));
          }
          this.showLoadFailed();
          this.hasError = true;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('get Stats Input failed:', err);
        this.showLoadFailed();
        this.isLoading = false;
        this.hasError = true;
      },
    });
  }
}
